// deps
import React, { useCallback } from "react";

// internal-deps
import { DialogHOC, isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import { CouponConfetti, CouponMessageCard } from "@swiggy-private/cart-types";

// constants
import { GIFTING_COUPON_MODAL_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { GIFTING_IMAGE, SPARKLE_ICON } from "./constants";

// dls
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// styles
import {
    GiftingImageContainer,
    Container,
    ContentContainer,
    StyledCloseIcon,
    IconWrapper,
    HeaderTitleWrapper,
    DescriptionText,
} from "./styles";

export interface GiftingCouponModalProps {
    couponSuccessData?: CouponMessageCard;
    couponErrorData?: CouponMessageCard;
    closeModal: VoidFunction;
    onImpressionGiftingModal: (payload: { title: string; subtitle: string; description: string }) => void;
    onClickCloseGiftingModal: (payload: { title: string; subtitle: string; description: string }) => void;
    onClickCTAGiftingModal: (payload: { title: string; subtitle: string; description: string }) => void;
    triggerConfettiByType?: (type: CouponConfetti) => void;
    showCouponConfetti?: boolean;
}

const GiftingCouponModal: React.FC<GiftingCouponModalProps> = ({
    couponSuccessData,
    couponErrorData,
    closeModal,
    onImpressionGiftingModal,
    onClickCloseGiftingModal,
    onClickCTAGiftingModal,
    triggerConfettiByType,
    showCouponConfetti,
}: GiftingCouponModalProps) => {
    const modalData = !isEmpty(couponSuccessData) ? couponSuccessData : couponErrorData;
    const primarySavingsText = modalData?.primarySavingsInfo?.text || modalData?.text;
    const primarySavingsSubtext = modalData?.primarySavingsInfo?.subtext || modalData?.subtext;
    const primaryTextToShowInMiddle = `${primarySavingsSubtext ?? ""} ${primarySavingsText ?? ""}`.trim();

    useEffectOnce(() => {
        if (!isEmpty(modalData) && showCouponConfetti) {
            triggerConfettiByType?.(CouponConfetti.TOP_UP);
        }
        onImpressionGiftingModal?.({
            title: modalData?.title || "",
            subtitle: primaryTextToShowInMiddle || primarySavingsSubtext || "",
            description: modalData?.description || "",
        });
    });

    // manual closing
    const onCloseModal = useCallback(
        (isCTA?: boolean) => {
            if (isCTA) {
                onClickCTAGiftingModal({
                    title: modalData?.title || "",
                    subtitle: primaryTextToShowInMiddle || primarySavingsSubtext || "",
                    description: modalData?.description || "",
                });
            } else {
                onClickCloseGiftingModal({
                    title: modalData?.title || "",
                    subtitle: primaryTextToShowInMiddle || primarySavingsSubtext || "",
                    description: modalData?.description || "",
                });
            }
            closeModal();
        },
        [
            closeModal,
            onClickCTAGiftingModal,
            modalData?.title,
            modalData?.description,
            onClickCloseGiftingModal,
            primaryTextToShowInMiddle,
            primarySavingsSubtext,
        ],
    );

    const handleCloseIconClick = useCallback(() => onCloseModal(false), [onCloseModal]);
    const handleCTAClick = useCallback(() => onCloseModal(true), [onCloseModal]);

    if (isEmpty(modalData)) {
        return null;
    }

    return (
        <Container data-testid={GIFTING_COUPON_MODAL_TEST_IDS.MODAL}>
            <ContentContainer>
                <GiftingImageContainer data-testid={GIFTING_COUPON_MODAL_TEST_IDS.IMAGE_CONTAINER}>
                    <Image
                        cloudinaryId={modalData.icon || GIFTING_IMAGE.IMAGE_ID}
                        height={GIFTING_IMAGE.HEIGHT}
                        width={GIFTING_IMAGE.WIDTH}
                        data-testid={GIFTING_COUPON_MODAL_TEST_IDS.IMAGE}
                        customBackground={{ light: "", dark: "" }}
                    />
                </GiftingImageContainer>

                {!isEmpty(modalData.title) && (
                    <HeaderTitleWrapper>
                        <IconWrapper
                            cloudinaryId={SPARKLE_ICON.IMAGE_ID}
                            alt={SPARKLE_ICON.ALT}
                            width={SPARKLE_ICON.WIDTH}
                            height={SPARKLE_ICON.HEIGHT}
                            leftIcon
                        />
                        <Text
                            data-testid={GIFTING_COUPON_MODAL_TEST_IDS.TITLE}
                            variant="V2.overline.neutral.bold_spaced"
                            color="primary"
                        >
                            {modalData.title}
                        </Text>
                        <IconWrapper
                            cloudinaryId={SPARKLE_ICON.IMAGE_ID}
                            alt={SPARKLE_ICON.ALT}
                            width={SPARKLE_ICON.WIDTH}
                            height={SPARKLE_ICON.HEIGHT}
                            rightIcon
                        />
                    </HeaderTitleWrapper>
                )}

                {(!isEmpty(primaryTextToShowInMiddle) || !isEmpty(modalData.title)) && (
                    <Text
                        data-testid={GIFTING_COUPON_MODAL_TEST_IDS.SUBTITLE}
                        variant="V2.heading2.neutral.black"
                        color="text_High_Emphasis"
                    >
                        {primaryTextToShowInMiddle || modalData.title}
                    </Text>
                )}

                {!isEmpty(modalData.description) && (
                    <DescriptionText
                        data-testid={GIFTING_COUPON_MODAL_TEST_IDS.DESCRIPTION}
                        variant="V2.body2.neutral.medium"
                        color="text_Med_Emphasis"
                    >
                        {modalData.description}
                    </DescriptionText>
                )}
            </ContentContainer>

            <StyledCloseIcon onClick={handleCloseIconClick}>
                <SpriteIconWeb iconName="clearFilled24" fillColor="text_Low_Emphasis" />
            </StyledCloseIcon>

            <SuperButton
                onClick={handleCTAClick}
                data-testid={GIFTING_COUPON_MODAL_TEST_IDS.CLOSE_BTN}
                buttonType="primary"
                buttonSize="medium"
                variant="fullwidth"
            >
                {modalData.cta.text}
            </SuperButton>
        </Container>
    );
};

export default DialogHOC(React.memo(GiftingCouponModal));
